import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { OrderState } from '@treadinc/horizon-api-spec';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';

import OrderDispatchActions from '~components/Order/OrderDispatchActions';
import {
  BottomColumn,
  CheckboxColumn,
  MainColumn,
  makeOrderDispatchCardTemplateColumns,
} from '~components/Order/ordersDispatchColumns';
import OrderCard from '~components/OrderCard/OrderCard';
import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';
import { ExpandedState } from '~store/OrdersDispatchStore';
import { getOrderCheckboxState } from '~utils/orders';

import OrderDispatchCardExpandedBlock from './OrderDispatchCardExpandedBlock';
import trackOrderDispatchTabViewed, {
  OrderDispatchCardExpandedBlockTab,
} from './trackOrderDispatchTabViewed';
import useIsOrderAwaitingResponse from './useIsOrderAwaitingResponse';
import useOrderDispatchHiddenFields from './useOrderDispatchHiddenFields';
import useOrderSearchMatches from './useOrderSearchMatches';

interface OrderDispatchCardProps {
  checkedJobs?: string[];
  companyHasChildCompanies: boolean;
  expandedState?: string;
  onAcceptOrder: () => void;
  onBulkAssignMultipleClick?: () => void;
  onCheckedStateChange?: (orderId: string, jobId?: string) => void;
  onCloneOrderClick: () => void;
  onCopyAndEditClick: () => void;
  onCreateJobFromOrderClick: () => void;
  onDownloadFilesClick?: () => void;
  onEditClick: () => void;
  onEmailLoadDigestClick?: () => void;
  onExpandedStateChange: (orderId: string) => void;
  onOrderStateChange: (nextOrderState: OrderState) => Promise<void>;
  onRejectOrder: () => void;
  onTextAllDriversClick: () => void;
  order: Order;
  readOnly: boolean;
}

const OrderDispatchCard = observer(
  ({
    checkedJobs,
    expandedState: expandedStateProp,
    onAcceptOrder,
    onBulkAssignMultipleClick,
    onCheckedStateChange,
    onCloneOrderClick,
    onCopyAndEditClick,
    onCreateJobFromOrderClick,
    onDownloadFilesClick,
    onEditClick,
    onEmailLoadDigestClick,
    onExpandedStateChange,
    onOrderStateChange,
    onRejectOrder,
    onTextAllDriversClick,
    order,
    readOnly,
  }: OrderDispatchCardProps) => {
    const theme = useTheme();
    const { ordersDispatchStore } = useStores();

    const expandedState =
      expandedStateProp || ordersDispatchStore.orderCardsDefaultExpandedState;
    const isOpen = expandedState === ExpandedState.OPEN;

    const { hideEquipmentType, hideMaterials, hidePickupAndDropoff, hideStartTime } =
      useOrderDispatchHiddenFields();

    const handleCheckedOrderStateChange = useCallback(() => {
      onCheckedStateChange?.(order.id);
    }, [onCheckedStateChange, order.id]);

    const handleCheckedJobStateChange = useCallback(
      (jobId: string) => {
        onCheckedStateChange?.(order.id, jobId);
      },
      [onCheckedStateChange, order.id],
    );

    const trackTabViewedIfCollapsed = useCallback(
      (tab: OrderDispatchCardExpandedBlockTab) => {
        if (!isOpen) {
          trackOrderDispatchTabViewed(order, tab, 'expanded');
        }
      },
      [isOpen, order],
    );

    const toggleExpandedState = useCallback(() => {
      onExpandedStateChange(order.id);
    }, [onExpandedStateChange, order.id]);

    const handleCardClick = useCallback(() => {
      trackTabViewedIfCollapsed(OrderDispatchCardExpandedBlockTab.JOBS);
      toggleExpandedState();
    }, [toggleExpandedState, trackTabViewedIfCollapsed]);

    const [isMessagingTabRequested, setIsMessagingTabRequested] = useState(false);

    const requestMessagingTab = useCallback(() => {
      setIsMessagingTabRequested(true);
      trackTabViewedIfCollapsed(OrderDispatchCardExpandedBlockTab.MESSAGING);
    }, [trackTabViewedIfCollapsed]);

    const clearMessagingTabRequest = useCallback(
      () => setIsMessagingTabRequested(false),
      [],
    );

    const isOrderAwaitingResponse = useIsOrderAwaitingResponse();
    const highlightAwaitingResponse = isOrderAwaitingResponse(order.id);

    const searchMatches = useOrderSearchMatches(order);

    const orderCheckboxState = getOrderCheckboxState({
      checkedJobIds: checkedJobs,
      isLoadingOrderJobs: ordersDispatchStore.isLoadingOrderJobs,
      orderId: order.id,
      orderJobs: ordersDispatchStore.orderJobs,
      readOnly,
    });

    return (
      <OrderCard
        sx={
          highlightAwaitingResponse
            ? {
                // 4px accent bar on the card's left edge (overrides the default 1px border).
                borderLeftWidth: '4px',
                borderLeftColor: theme.brandV2.colors.treadOrange,
              }
            : undefined
        }
      >
        <Box
          onClick={handleCardClick}
          sx={{
            display: 'grid',
            gridTemplateColumns: makeOrderDispatchCardTemplateColumns(false),
            p: 1,
            rowGap: 1,
          }}
          data-ui={`page-dispatch__order-dispatch__order-${order.orderId}__button`}
        >
          <CheckboxColumn
            {...orderCheckboxState}
            onCheckedStateChange={handleCheckedOrderStateChange}
          />

          <MainColumn
            hideMaterialsSettingEnabled={hideMaterials}
            order={order}
            searchMatches={searchMatches}
          />

          <OrderDispatchActions
            isExpanded={isOpen}
            onAccept={onAcceptOrder}
            onBulkAssignMultiple={onBulkAssignMultipleClick}
            onCancel={() => onOrderStateChange(OrderState.CANCELED)}
            onCloneOrder={onCloneOrderClick}
            onCopyAndEdit={onCopyAndEditClick}
            onDownloadFiles={onDownloadFilesClick}
            onEdit={onEditClick}
            onEmailLoadDigest={onEmailLoadDigestClick}
            onMessagingTabRequest={requestMessagingTab}
            onOrderStateChange={onOrderStateChange}
            onReject={onRejectOrder}
            onTextAllDrivers={onTextAllDriversClick}
            onToggleExpanded={toggleExpandedState}
            order={order}
          />

          <BottomColumn
            order={order}
            hidePickupAndDropoffColumnSettingEnabled={hidePickupAndDropoff}
            hideEquipmentTypeSettingEnabled={hideEquipmentType}
            hideOrderStartTimeSettingEnabled={hideStartTime}
          />
        </Box>
        <Fade mountOnEnter unmountOnExit in={isOpen}>
          <Box
            sx={{
              borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            }}
          >
            <OrderDispatchCardExpandedBlock
              checkedJobs={checkedJobs}
              isMessagingTabRequested={isMessagingTabRequested}
              onCheckedJobStateChange={handleCheckedJobStateChange}
              onCreateJobFromOrder={onCreateJobFromOrderClick}
              onMessagingTabRequestHandled={clearMessagingTabRequest}
              order={order}
              readOnly={readOnly}
            />
          </Box>
        </Fade>
      </OrderCard>
    );
  },
);

export default OrderDispatchCard;
